<template>
  <Page>
    <div class="max-w-6xl" data-cy="api-access-page">
      <h1 class="page-header-1 mb-4">API access</h1>

      <nav
        class="mb-4 border-b border-gray-200"
        aria-label="API access sections"
      >
        <ul class="-mb-px flex flex-wrap text-sm font-medium text-gray-500">
          <li v-for="tab in TABS" :key="tab.key" class="me-2">
            <a
              :href="tabHref(tab.key)"
              class="inline-block rounded-t-lg border-b-2 p-4"
              :class="
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent hover:border-gray-300 hover:text-gray-600'
              "
              :aria-current="activeTab === tab.key ? 'page' : undefined"
              :data-cy="`api-access-tab:${tab.key}`"
              @click.prevent="selectTab(tab.key)"
              >{{ tab.label }}</a
            >
          </li>
        </ul>
      </nav>

      <ApiKeys v-if="activeTab === 'keys'" @settled="setTrail" />
      <ApiDocs v-else @settled="setTrail" />
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import { API_ACCESS_PATH, docsHref } from "./apiAccess";
import ApiDocs from "./ApiDocs.vue";
import ApiKeys from "./ApiKeys.vue";

// The tab lives in the query string, as on the webhooks hub: the header
// breadcrumbs are rebuilt on every path change.
const TABS = [
  { key: "keys", label: "Keys" },
  { key: "docs", label: "Docs" }
] as const;
type TabKey = (typeof TABS)[number]["key"];

const store = useStore();
const route = useRoute();
const router = useRouter();

const activeTab = computed<TabKey>(() =>
  route.query.tab === "docs" ? "docs" : "keys"
);

function tabHref(key: TabKey): string {
  return key === "docs" ? docsHref("overview") : API_ACCESS_PATH;
}

function selectTab(key: TabKey) {
  if (key === activeTab.value) return;
  router.replace({ query: key === "docs" ? { tab: "docs" } : {} });
}

// Drive the header breadcrumbs (Marketplace › API access) as the webhooks hub
// does: set after the tab's first load, success or failure, so the trail
// lands after the router's deferred regeneration.
function setTrail() {
  store.dispatch("setBreadcrumbs", [
    { label: "Marketplace", url: "/marketplace" },
    { label: "API access" }
  ]);
}
</script>
